import { BulkOptionType, DistributorDataTypeV2 } from 'elements/hardwareSelectorV2/types'
import { ForecastStatus } from 'pages/inventory/forecast/type'
import type { ComponentTypes, ComponentTypesV2 } from 'types/selectComponent'
import { generateUUID } from 'util/misc'
import {
  getTotalSavingFromFixedAmountDiscount,
  getTotalSavingFromPercentageDiscount,
  isMinimumAmountRequirement,
  isPercentageDiscount,
} from './discountUtils'
import type {
  DiscountType,
  DistributorDataType,
  HardwareSupplierFilterKeyType,
  InventoryForecastData,
  LineItemLoadedDataType,
  LineItemStatus,
  ProjectOrderDetail,
  StockLevelType,
} from './type'

// TODO: consider to group lazy loaded values into a separate key
export class OrderLineItem {
  uuid: string
  code: string
  selectedDistributor: HardwareSupplierFilterKeyType | 'component' | undefined
  selectedDistributorOrderingData?: DistributorDataType
  variantId?: string // Todo: replace usage by selectedDistributorOrderingData
  variantDiscounts?: DiscountType[] // Todo: replace usage by selectedDistributorOrderingData
  quantityPerItem?: number // Todo: replace usage by selectedDistributorOrderingData
  stockLevels?: StockLevelType[] // Todo: replace usage by selectedDistributorOrderingData
  pricePerUnit?: number // Todo: replace usage by selectedDistributorOrderingData
  quantity: number
  status: LineItemStatus
  projectOrder?: ProjectOrderDetail[]
  orgId?: number
  confirmed: boolean
  componentType?: ComponentTypes
  data?: LineItemLoadedDataType
  allocatedQuantity?: number
  requiredQuantity?: number
  validity?: ForecastStatus
  inventoryForecastData?: InventoryForecastData
  otherComponentType?: ComponentTypesV2
  componentTypeV2?: ComponentTypesV2
  orderingData?: DistributorDataTypeV2[]
  componentCost?: number // Manual price as set in control.
  org_logo_public_url?: string
  org_name?: string
  bulkOption?: BulkOptionType | null
  rating?: number
  bomLineItemId?: string
  componentTitle?: string // "title" data field of the component - is only populated by "other" type components
  constructor({
    code,
    variantId,
    selectedDistributor,
    selectedDistributorOrderingData,
    quantityPerItem,
    quantity,
    pricePerUnit,
    status,
    orgId,
    componentType,
    variantDiscounts,
    projectOrder,
    stockLevels,
    data,
    confirmed,
    inventoryForecastData,
    componentTypeV2,
    otherComponentType,
    orderingData,
    componentCost,
    org_logo_public_url,
    org_name,
    bulkOption,
    rating,
    bomLineItemId,
    componentTitle,
  }: {
    code: string
    variantId?: string
    quantityPerItem?: number
    quantity: number
    pricePerUnit?: number
    orgId?: number
    confirmed?: boolean
    status?: LineItemStatus
    selectedDistributor: HardwareSupplierFilterKeyType | 'component' | undefined
    selectedDistributorOrderingData?: DistributorDataType | DistributorDataTypeV2 | undefined
    variantDiscounts?: DiscountType[]
    projectOrder?: ProjectOrderDetail[]
    stockLevels?: StockLevelType[]
    componentType?: ComponentTypes
    data?: LineItemLoadedDataType
    inventoryForecastData?: InventoryForecastData
    componentTypeV2?: ComponentTypesV2
    otherComponentType?: ComponentTypesV2
    orderingData?: DistributorDataTypeV2[]
    componentCost?: number
    org_logo_public_url?: string
    org_name?: string
    bulkOption?: BulkOptionType | null
    rating?: number
    bomLineItemId?: string
    componentTitle?: string
  }) {
    this.uuid = generateUUID()
    this.code = code
    this.variantId = variantId
    this.selectedDistributor = selectedDistributor
    this.selectedDistributorOrderingData = selectedDistributorOrderingData
    this.variantDiscounts = variantDiscounts
    this.pricePerUnit = pricePerUnit
    this.quantityPerItem = quantityPerItem
    this.quantity = quantity
    this.status = status || 'loading'
    this.orgId = orgId
    this.stockLevels = stockLevels != null ? [...stockLevels] : undefined
    this.projectOrder = projectOrder != null ? [...projectOrder] : undefined
    this.componentType = componentType
    this.data = data
    this.confirmed = confirmed || false
    this.setDefaultSelectedVariant()
    this.inventoryForecastData = inventoryForecastData
    this.componentTypeV2 = componentTypeV2
    this.otherComponentType = otherComponentType
    this.orderingData = orderingData
    this.componentCost = componentCost
    this.org_logo_public_url = org_logo_public_url
    this.org_name = org_name
    this.bulkOption = bulkOption
    this.rating = rating
    this.bomLineItemId = bomLineItemId
    this.componentTitle = componentTitle
  }

  get bulkQuantity() {
    if (!this.bulkOption) return undefined
    return this.quantity / this.bulkOption.quantity_per_item
  }

  get bulkPrice() {
    if (!this.bulkOption) return undefined
    return this.bulkOption.price_per_unit * this.quantity
  }

  private selectedVariantHealthyCheck = (): boolean => {
    if (!this.selectedDistributor) {
      console.log(`component(${this.code}) attempted healthy variant check without selectedDistributor being set`)
      return false
    }
    if (this.variantId !== this.selectedDistributorOrderingData?.variant_id) {
      console.log(`component(${this.code}) variant id not matched with selected distributor`)
      return false
    }
    const variant = this.data?.distributors?.find(
      (variant) => variant.variant_id === this.variantId && variant.distributor === this.selectedDistributor
    )
    if (variant == null) {
      console.log(`component(${this.code}) variant not found in loaded data`)
      return false
    }
    return true
  }

  static getDefaultSelectedVariant(
    distributors: DistributorDataType[],
    selectedDistributor: HardwareSupplierFilterKeyType
  ): DistributorDataType | undefined {
    // Hightest priority default to available individual variant in primary warehouse
    const defaultDistributorPrimaryWarehouse = distributors.find(
      (distributor) =>
        (distributor.stock_levels?.length || 0) > 0 &&
        distributor.is_available &&
        distributor.quantity_per_item === 1 &&
        distributor.distributor === selectedDistributor
    )

    if (defaultDistributorPrimaryWarehouse !== undefined) {
      return defaultDistributorPrimaryWarehouse
    }

    // If not best matched variant then default to available individual
    const defaultDistributorAvailable = distributors.find(
      (distributor) =>
        distributor.is_available &&
        distributor.quantity_per_item === 1 &&
        distributor.distributor === selectedDistributor
    )

    if (defaultDistributorAvailable !== undefined) {
      return defaultDistributorAvailable
    }

    // fallback to any individual variant
    const defaultIndividualDistributor = distributors?.find(
      (distributor) => distributor.quantity_per_item === 1 && distributor.distributor === selectedDistributor
    )

    if (defaultIndividualDistributor !== undefined) {
      return defaultIndividualDistributor
    }
  }

  private setDefaultSelectedVariant(): boolean {
    this.selectedVariantHealthyCheck()
    if (this.variantId !== undefined) {
      this.switchVariant(this.variantId)
      return true
    }

    const defaultSelectedVariant =
      this.data?.distributors &&
      OrderLineItem.getDefaultSelectedVariant(
        this.data.distributors,
        this.selectedDistributor as HardwareSupplierFilterKeyType
      )

    if (defaultSelectedVariant) {
      this.switchVariant(defaultSelectedVariant.variant_id)
      return true
    }
    return false
  }

  switchVariant(variantId: string): boolean {
    const distributor = this.data?.distributors?.find(
      (distributor: DistributorDataType) =>
        distributor.variant_id === variantId && distributor.distributor === this.selectedDistributor
    )
    if (distributor == null) {
      return false
    }
    this.selectedDistributorOrderingData = distributor
    this.pricePerUnit = distributor.price
    this.quantityPerItem = distributor.quantity_per_item
    this.stockLevels = distributor.stock_levels
    this.variantId = distributor.variant_id
    this.variantDiscounts = distributor.volume_discounts
    return true
  }

  isDiscountAvailable(discountId: string): boolean {
    const discount = this.selectedDistributorOrderingData?.volume_discounts.find(
      (discount: DiscountType) => discount.id && discount.id === discountId
    )
    if (!discount) {
      return false
    }
    if (isMinimumAmountRequirement(discount.minimumRequirement)) {
      const currentAmount = this.getTotalCostBeforeDiscount()
      if (currentAmount == null) return false
      return currentAmount >= parseInt(discount.minimumRequirement.greaterThanOrEqualToSubtotal.amount)
    } else {
      return this.quantity >= parseInt(discount.minimumRequirement.greaterThanOrEqualToQuantity)
    }
  }

  getTotalCostBeforeDiscount(): number | undefined {
    if (this.pricePerUnit == null) {
      return undefined
    }
    return this.pricePerUnit * this.quantity
  }

  getBestDiscountOffer(
    distributor?: HardwareSupplierFilterKeyType | 'component',
    isFromQuote: boolean = false
  ): {
    beforeDiscount?: number
    afterDiscount?: number
    discount?: DiscountType
  } {
    const offer = {}

    if (this.bulkOption) {
      return {
        beforeDiscount: this.bulkPrice,
        afterDiscount: this.bulkPrice,
        discount: undefined,
      }
    }

    // Handle component pricing
    if (distributor === 'component' || isFromQuote) {
      const costBeforeDiscount = this.componentCost ? this.componentCost * this.quantity : 0
      return {
        beforeDiscount: costBeforeDiscount,
        afterDiscount: costBeforeDiscount,
        discount: undefined,
      }
    }

    // Determine which ordering data to use
    let orderingData: DistributorDataType | DistributorDataTypeV2 | undefined
    let costBeforeDiscount: number | undefined

    if (distributor) {
      // Use specific distributor from orderingData
      orderingData = this.orderingData?.find((item) => item.distributor === distributor)
      costBeforeDiscount = orderingData?.price ? orderingData.price * this.quantity : undefined
    } else {
      // Use selectedDistributorOrderingData (original behavior)
      orderingData = this.selectedDistributorOrderingData
      costBeforeDiscount = this.getTotalCostBeforeDiscount()
    }

    if (costBeforeDiscount == null || !orderingData) {
      return offer
    }

    offer['beforeDiscount'] = costBeforeDiscount
    offer['afterDiscount'] = costBeforeDiscount

    if (!orderingData.volume_discounts?.length) {
      return offer
    }

    const discounts = orderingData.volume_discounts

    discounts.forEach((discount) => {
      // Use the existing isDiscountAvailable method for consistency
      if (!this.isDiscountAvailable(discount.id)) {
        return
      }

      let saving: number
      if (isPercentageDiscount(discount)) {
        saving = getTotalSavingFromPercentageDiscount(discount.customerGets.value, offer['beforeDiscount'])
      } else {
        saving = getTotalSavingFromFixedAmountDiscount({
          discountValue: discount.customerGets.value,
          quantity: this.quantity,
        })
      }
      if (offer['afterDiscount'] > offer['beforeDiscount'] - saving) {
        offer['discount'] = discount
        offer['afterDiscount'] = offer['beforeDiscount'] - saving
      }
    })

    return offer
  }
}

export default OrderLineItem
